import { state, modes } from "./state.js";
import { screen } from "./screen.js";
import { columns, rows } from "./setup.js";
import { commands } from "./commands.js";
import { Cell, cellsToString } from "./cell.js";
import { BaseError, SyntaxError } from "./errors.js";
import { printLine } from "./utilities.js";
import { BASIC } from "./BASIC.js";

export const processKey = (key) => {
    // If not ready, ignore the key.
    if (!state.ready) return;
    // Push the key to the current input.
    state.currentInput.push(new Cell(key.toUpperCase()));

    // Push the key to the current line.
    // Add a new line if the line doesn't exist yet.
    if (!screen.lines[screen.cursorPosition[0]]) {
        screen.lines[screen.cursorPosition[0]] = screen.emptyLine();
    }
    // If the current line is full, move to the next line.
    screen.lines[screen.cursorPosition[0]][screen.cursorPosition[1]] = new Cell(key.toUpperCase());
    // Update the cursor position.
    screen.cursorPosition[1]++;
    // If the cursor position exceeds the number of columns, move to the next line.
    if (screen.cursorPosition[1] >= columns) {
        screen.cursorPosition[1] = 0;
        screen.cursorPosition[0]++;
    }
    // If the cursor position exceeds the number of rows, move all lines up one line and reset the cursor position to last line.
    if (screen.cursorPosition[0] >= rows) {
        shiftLinesUp();
    }
};

export const processEnter = async (processCommand = true) => {
  // If not ready, ignore the key.
  if (!state.ready) return;
  // Move the cursor to the start of the next line.
  screen.cursorPosition[0]++;
  screen.cursorPosition[1] = 0;
  // If the cursor position exceeds the number of rows, move all lines up one line and reset the cursor position to last line.
  if (screen.cursorPosition[0] >= rows) {
    shiftLinesUp();
  }

  // If the command must be processed, we need to convert the current input into a string.
  if (processCommand) {
    processCommandInput(state.currentInput);
  }

  // Reset the current input.
  state.currentInput = [];
};

export const processBackspace = () => {
    // If not ready, ignore the key.
    if (!state.ready) return;
    state.currentInput = state.currentInput.slice(0, -1);
    // Remove the last character from the current line.
    screen.lines[screen.cursorPosition[0]][screen.cursorPosition[1] - 1] = new Cell(" ");
    // Move the cursor to the left.
    screen.cursorPosition[1]--;
    // If the cursor position is less than 0, set it to 0. It cannot move to a previous line.
    if (screen.cursorPosition[1] < 0) {
      screen.cursorPosition[1] = 0;
    }
};

export const processCommandInput = async (input) => {
  // If the input is an array of cells, convert it to a string.
  if (Array.isArray(input)) {
    input = cellsToString(input);
  }
  input = input.trim();
  if (input === "") return;

  // Check if the first character(s) of the input is a number.
  // If it is, it is a line number. That means we're in program mode.
  // If the first character(s) is not a number, we're in input mode.
  // check first character(s) of the input
  if (!isNaN(input[0])) {
    await processProgramInput(input);
    return;
  }

  state.mode = modes.INPUT;
  state.ready = false;
  state.blockCursor = true;

  // Get all command keys and sort them descending by length.
  // Sorting by length ensures that longer commands (e.g., PRINT#) are tried before shorter ones.
  const commandKeys = Object.keys(commands).sort((a, b) => b.length - a.length);

  let foundCommand = null;
  let args = "";

  // Try to match a command key at the very start of the input.
  for (const key of commandKeys) {
    // Compare in a case-insensitive manner.
    if (input.toUpperCase().startsWith(key.toUpperCase())) {
      // Command match found.
      foundCommand = key;

      // Extract the arguments by slicing the input after the command key.
      args = input.slice(key.length).trim();
      break;
    }
  }

  try {
    if (foundCommand) {
      // Execute the matched command with the extracted arguments.
      await commands[foundCommand](args);
    } else {
      // If no command is found, handle it as a syntax error.
      throw new SyntaxError();
    }
  }
  catch (error) {
    // Handle specific error types if needed.
    if (error instanceof BaseError) {
      await error.handleError();
    } else {
      console.error(error);
      await new SyntaxError().handleError();
    }
  }
  finally {
    // Reset the state to ready and unblock the cursor.
    state.ready = true;
    state.blockCursor = false;
    // Reset the current input.
    state.currentInput = [];
    await printLine("READY.");
  }
};

const processProgramInput = async (input) => {
  // Convert the input to a string.
  if (Array.isArray(input)) {
    input = cellsToString(input);
  }
  input = input.trim();

  // Get the line number from the input. Line number can be any number.
  // To get the line number, we need to find the first non-number character.
  const lineNumberMatch = input.match(/^(\d+)\s*/); // Match one or more digits at the start of the string.
  if (lineNumberMatch) {
    const lineNumber = parseInt(lineNumberMatch[1], 10); // Convert the matched line number to an integer.
    // Get the rest of the input as the program line.
    const programLine = input.slice(lineNumberMatch[0].length).trim();
    // If the program line is empty, remove the line from the custom program.
    if (programLine === "") {
      delete BASIC.lines[lineNumber];
    } else {
      // Store the line number and program line in the custom program.
      BASIC.lines[lineNumber] = programLine;
    }

    state.loadedProgram = BASIC;
    state.mode = modes.BASIC;
  }
};

export const processArrowKey = (e) => {
  // If not ready, ignore the key.
  if (!state.ready) return;
  // Move the cursor in the specified direction.
  switch (e.key) {
    case "ArrowUp":
      screen.cursorPosition[0]--;
      if (screen.cursorPosition[0] < 0) {
        screen.cursorPosition[0] = 0;
      }
      break;
    case "ArrowDown":
      screen.cursorPosition[0]++;
      if (screen.cursorPosition[0] >= rows) {
        screen.cursorPosition[0] = rows - 1;
      }
      break;
    case "ArrowLeft":
      screen.cursorPosition[1]--;
      if (screen.cursorPosition[1] < 0) {
        screen.cursorPosition[1] = 0;
      }
      break;
    case "ArrowRight":
      screen.cursorPosition[1]++;
      if (screen.cursorPosition[1] >= columns) {
        screen.cursorPosition[1] = columns - 1;
      }
      break;
  }
};

export const shiftLinesUp = () => {
  // Move all lines up one line.
  screen.lines.shift();
  // Add a new empty line at the end.
  screen.lines.push(screen.emptyLine());
  // Reset the cursor position to the last line.
  screen.cursorPosition[0] = rows - 1;
};